import { OrderDirection } from "_shared/_api/ordering/order-direction";
import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import {
  ProposalBoxType,
  ProposalContext,
  ProposalEvent,
  ProposalOrderField,
  ProposalStatus,
} from "work/proposals/__constants/constants";
import Session from "users/__session/session";
import type { ProposalfilterParametersProps } from "work/proposals/_api/queries/_schemas/proposal-filter-schema";
import type { ProposalorderParametersProps } from "work/proposals/_api/queries/_schemas/proposal-order-schema";
import ProposalQuerySchema, {
  type ProposalQueryJSON,
  type ProposalQueryParams,
} from "work/proposals/_api/queries/_schemas/proposal-query-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { Query } from "_shared/_api/queries/query";

export default class ProposalQuery extends Query<
  ProposalFilterParameters,
  ProposalOrderParameters,
  PaginationParameters
> {
  public static fromJSON(json: ProposalQueryJSON): ProposalQuery {
    if (!json) return json;
    const parsed = ProposalQuerySchema.validate(json);
    return new ProposalQuery(parsed);
  }

  public constructor(params: ProposalQueryParams) {
    super({
      filterParameters: new ProposalFilterParameters(params.filterParameters),
      orderParameters: new ProposalOrderParameters(params.orderParameters),
      paginationParameters: PaginationParameters.create(params.paginationParameters),
    });
  }

  public toJSON(): ProposalQueryJSON {
    return ProposalQuerySchema.serialize(this);
  }

  public isEqualTo(other: ProposalQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

export class ProposalFilterParameters {
  public static fromSearchParams(
    searchParams: URLSearchParams,
    session: Session,
    boxType?: ProposalBoxType
  ): ProposalFilterParameters {
    const statusFilter = searchParams.get("status");
    const negotiableFilter = searchParams.get("negotiable");
    const filterParameters = new ProposalFilterParameters({
      query: searchParams.get("query") ?? undefined,
      status: statusFilter ? ProposalStatus[statusFilter as keyof typeof ProposalStatus] : undefined,
      name: searchParams.get("name") ?? undefined,
      isNegotiable: negotiableFilter ? negotiableFilter === "true" : undefined,
      context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
      boxType: boxType,
    });
    return filterParameters;
  }

  public readonly query?: string;

  public readonly RFPId?: Guid;

  public readonly name?: string;

  public readonly description?: string;

  public readonly clientUserId?: Guid;
  public readonly clientEntityId?: Guid;
  public readonly clientTeamMemberUserId?: Guid;
  public readonly clientTeamMemberEntityId?: Guid;

  public readonly hasStart?: boolean;
  public readonly minStart?: AHDate;
  public readonly maxStart?: AHDate;

  public readonly hasEnd?: boolean;
  public readonly minEnd?: AHDate;
  public readonly maxEnd?: AHDate;

  public readonly vendorTeamRestricted?: boolean;
  public readonly vendorUserId?: Guid;
  public readonly vendorEntityId?: Guid;
  public readonly vendorTeamMemberUserId?: Guid;
  public readonly vendorTeamMemberEntityId?: Guid;

  public readonly additionalVendorUserId?: Guid;
  public readonly additionalVendorEntityId?: Guid;

  public readonly feeScheduleCategoryId?: Guid;
  public readonly feeScheduleCategoryName?: string;
  public readonly feeScheduleCategoryDescription?: string;

  public readonly teamTemplateId?: Guid;
  public readonly teamTemplateName?: string;
  public readonly feeScheduleTemplateId?: Guid;
  public readonly feeScheduleTemplateName?: string;

  public readonly minDiscount?: number;
  public readonly maxDiscount?: number;

  public readonly minCreated?: AHDate;
  public readonly maxCreated?: AHDate;

  public readonly isNegotiable?: boolean;

  public readonly hasResponseDueBy?: boolean;
  public readonly minResponseDueBy?: AHDate;
  public readonly maxResponseDueBy?: AHDate;

  public readonly isSuperseded?: boolean;
  public readonly supersededByProposalId?: Guid;

  public readonly clientReviewerId?: Guid;
  public readonly vendorReviewerId?: Guid;

  public readonly status?: ProposalStatus;

  public readonly event?: ProposalEvent;
  public readonly eventActorUserId?: Guid;
  public readonly minEventDate?: AHDate;
  public readonly maxEventDate?: AHDate;

  public readonly boxType?: ProposalBoxType;
  public readonly context?: ProposalContext;

  public readonly isDeleted?: boolean;
  public readonly maxDeleted?: AHDate;
  public readonly minDeleted?: AHDate;

  public readonly userId?: Guid;

  public constructor(params: ProposalfilterParametersProps = {}) {
    this.query = params.query;
    this.RFPId = params.RFPId;
    this.name = params.name;
    this.description = params.description;
    this.clientUserId = params.clientUserId;
    this.clientEntityId = params.clientEntityId;
    this.clientTeamMemberUserId = params.clientTeamMemberUserId;
    this.clientTeamMemberEntityId = params.clientTeamMemberEntityId;
    this.hasStart = params.hasStart;
    this.minStart = params.minStart;
    this.maxStart = params.maxStart;
    this.hasEnd = params.hasEnd;
    this.minEnd = params.minEnd;
    this.maxEnd = params.maxEnd;
    this.vendorTeamRestricted = params.vendorTeamRestricted;
    this.vendorUserId = params.vendorUserId;
    this.vendorEntityId = params.vendorEntityId;
    this.vendorTeamMemberUserId = params.vendorTeamMemberUserId;
    this.vendorTeamMemberEntityId = params.vendorTeamMemberEntityId;
    this.additionalVendorUserId = params.additionalVendorUserId;
    this.additionalVendorEntityId = params.additionalVendorEntityId;
    this.feeScheduleCategoryId = params.feeScheduleCategoryId;
    this.feeScheduleCategoryName = params.feeScheduleCategoryName;
    this.feeScheduleCategoryDescription = params.feeScheduleCategoryDescription;
    this.teamTemplateId = params.teamTemplateId;
    this.teamTemplateName = params.teamTemplateName;
    this.feeScheduleTemplateId = params.feeScheduleTemplateId;
    this.feeScheduleTemplateName = params.feeScheduleTemplateName;
    this.minDiscount = params.minDiscount;
    this.maxDiscount = params.maxDiscount;
    this.minCreated = params.minCreated;
    this.maxCreated = params.maxCreated;
    this.isNegotiable = params.isNegotiable;
    this.hasResponseDueBy = params.hasResponseDueBy;
    this.minResponseDueBy = params.minResponseDueBy;
    this.maxResponseDueBy = params.maxResponseDueBy;
    this.isSuperseded = params.isSuperseded;
    this.supersededByProposalId = params.supersededByProposalId;
    this.clientReviewerId = params.clientReviewerId;
    this.vendorReviewerId = params.vendorReviewerId;
    this.status = params.status;
    this.event = params.event;
    this.eventActorUserId = params.eventActorUserId;
    this.minEventDate = params.minEventDate;
    this.maxEventDate = params.maxEventDate;
    this.boxType = params.boxType;
    this.context = params.context;
    this.isDeleted = params.isDeleted;
    this.maxDeleted = params.maxDeleted;
    this.minDeleted = params.minDeleted;
    this.userId = params.userId;
  }

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(this)) {
      if (value !== undefined) {
        params.append(key, value?.toString() ?? "null");
      }
    }
    return params;
  }

  public isEqualTo(other: ProposalFilterParameters): boolean {
    return Object.entries(this).every(([key, value]) => value === other[key as keyof ProposalFilterParameters]);
  }
}

export class ProposalOrderParameters {
  public static fromSearchParams(searchParams: URLSearchParams): ProposalOrderParameters {
    let orderBy = ProposalOrderField.Created;
    let orderDir = OrderDirection.Descending;

    const orderByParam = searchParams.get("orderBy");
    const orderDirParam = searchParams.get("orderDir");

    if (orderByParam) {
      const enumValue = Object.values(ProposalOrderField).find(
        (field) => field.toLowerCase() === orderByParam.toLowerCase()
      );
      if (enumValue) {
        orderBy = enumValue;
      }
    }

    if (orderDirParam) {
      const enumValue = Object.values(OrderDirection).find(
        (field) => field.toLowerCase() === orderDirParam.toLowerCase()
      );
      if (enumValue) {
        orderDir = enumValue;
      }
    }

    return new ProposalOrderParameters({ orderBy, orderDir });
  }

  public readonly orderBy: ProposalOrderField;
  public readonly orderDir: OrderDirection;

  constructor(params: ProposalorderParametersProps) {
    this.orderBy = params.orderBy;
    this.orderDir = params.orderDir;
  }

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    params.append("orderBy", this.orderBy);
    params.append("orderDir", this.orderDir);
    return params;
  }

  public isEqualTo(other: ProposalOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }
}
